import { EventBus } from '../engine/events';
import type { LumineInstance } from '../game/lumine';

/**
 * Store di stato centralizzato (single source of truth).
 * Tutto ciò che deve sopravvivere a un salvataggio — o, in Fase 2, essere
 * sincronizzato col server — vive qui dentro come dati puri serializzabili.
 */

export type GamePhase = 'boot' | 'intro' | 'explore' | 'battle' | 'menu' | 'gameover';

export interface PlayerState {
  name: string;
  pos: { x: number; y: number };
  dir: 'su' | 'giu' | 'sinistra' | 'destra';
  team: LumineInstance[];
  inventory: {
    risonatori: number; // dispositivi di cattura/stabilizzazione
  };
}

export interface GameState {
  version: 1;
  phase: GamePhase;
  player: PlayerState;
  /** Ora del giorno normalizzata [0,1): 0 = alba, 0.5 = tramonto. */
  timeOfDay: number;
  /** Faglie stabilizzate (progressione). */
  stabilizedAnomalies: string[];
}

export interface StoreEvents extends Record<string, unknown> {
  phaseChanged: { from: GamePhase; to: GamePhase };
  teamChanged: { team: LumineInstance[] };
  inventoryChanged: { risonatori: number };
  saved: { at: number };
  notice: { text: string };
}

export function createInitialState(): GameState {
  return {
    version: 1,
    phase: 'boot',
    player: {
      name: 'Custode',
      pos: { x: 0, y: 0 },
      dir: 'giu',
      team: [],
      inventory: { risonatori: 8 },
    },
    timeOfDay: 0.2, // mattina
    stabilizedAnomalies: [],
  };
}

export class GameStore {
  readonly events = new EventBus<StoreEvents>();
  state: GameState;

  constructor(initial?: GameState) {
    this.state = initial ?? createInitialState();
  }

  setPhase(to: GamePhase): void {
    const from = this.state.phase;
    if (from === to) return;
    this.state.phase = to;
    this.events.emit('phaseChanged', { from, to });
  }

  addToTeam(lumine: LumineInstance): boolean {
    if (this.state.player.team.length >= 6) return false;
    this.state.player.team.push(lumine);
    this.events.emit('teamChanged', { team: this.state.player.team });
    return true;
  }

  notifyTeamChanged(): void {
    this.events.emit('teamChanged', { team: this.state.player.team });
  }

  useRisonatore(): boolean {
    if (this.state.player.inventory.risonatori <= 0) return false;
    this.state.player.inventory.risonatori -= 1;
    this.events.emit('inventoryChanged', {
      risonatori: this.state.player.inventory.risonatori,
    });
    return true;
  }

  notice(text: string): void {
    this.events.emit('notice', { text });
  }
}
